import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Siren, RefreshCw, ServerCrash, Radio, Activity, ShieldAlert, PlugZap, ChevronDown, ChevronRight,
  Check, X, Star, Info, FileText, AlertTriangle, CircleCheck,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTenantScope } from '../context/TenantScopeContext';
import { CONSOLE, BAND_COLORS, SERIES_OPENED, SERIES_CLOSED, GRID_INK, AXIS_INK, SEVERITY_DARK } from '../theme/console';
import OutcomeBadge, { EventOutcome } from '../components/OutcomeBadge';
import TenantSelect from '../components/TenantSelect';

// I colori dei grafici vivono ora in theme/console.ts, condivisi con Overview: là c'è anche
// l'esito della validazione della palette contro la superficie scura su cui rendono.

interface PriorityFactor { label: string; points: number }

interface CasePriority {
  score: number;
  factors: PriorityFactor[];
  suggestedAction: string;
  rationale: string;
}

interface Decision {
  case_id: number;
  chosen_action: string;
  suggested_action: string | null;
  note: string | null;
  created_at: string;
  user_name: string | null;
}

interface QueuedCase {
  id: number;
  tenant_id: number;
  partner_id: number;
  tenant_name: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  entity_host: string | null;
  asset_id: number | null;
  business_critical: boolean;
  created_at: string;
  acknowledged_at: string | null;
  assigned_user: string | null;
  alert_count: number;
  outcome: EventOutcome | null;
  priority: CasePriority;
  last_decision: Decision | null;
}

interface DashboardData {
  urgent: {
    priorityBands: { critical: number; medium: number; low: number };
    suggestedActions: Record<string, number>;
    priorityQueue: QueuedCase[];
    criticalAssetsOffline: { case_id: number; entity_host: string; title: string; tenant_name: string; asset_id: number | null; ip_address: string | null; created_at: string }[];
    brokersOffline: { id: number; name: string; site_name: string; tenant_name: string; offline_minutes: number | null }[];
    dataQualityIssues: { id: number; type: string; severity: string; message: string; tenant_name: string; last_seen_at: string }[];
    compromisedEndpoints: { asset_id: number; hostname: string; tenant_name: string; vendor: string; infected: boolean; isolated: boolean; active_threats: number | null; business_critical: boolean }[];
    integrationIssues: { kind: string; label: string; scope_name: string; last_sync_at: string | null; last_sync_status: string | null; last_error: string | null }[];
  };
  summary: {
    cases: { cases_24h: string; cases_new: string; cases_open: string; cases_closed_24h: string };
    assets: { assets_total: string; assets_new_today: string; assets_business_critical: string; vulnerabilities_high: string };
    ruleCounters: { name: string; count: string }[];
    trend: { day: string; opened: string; closed: string }[];
  };
  meta: { suggestionEngine: string; queueScanned: number; queueTruncated: boolean };
}

const ACTION_LABELS: Record<string, string> = {
  soar_isolate_host: 'Isola host',
  soar_block_ip: 'Blocca IP',
  contact_customer: 'Contatta il cliente',
  send_report: 'Invia report',
  monitor: 'Monitora',
  likely_false_positive: 'Probabile falso positivo',
  other: 'Altra azione',
};

// Le azioni SOAR restano suggeribili e registrabili ma non eseguibili: il motore di orchestrazione
// è la Fase 3 della roadmap, mai iniziata. Meglio dirlo nell'interfaccia che offrire un pulsante
// che non farebbe nulla.
const SOAR_ACTIONS = ['soar_isolate_host', 'soar_block_ip'];

const SEVERITY_BADGE = SEVERITY_DARK;

const DQ_TYPE_LABELS: Record<string, string> = {
  broker_offline: 'Broker offline', no_data: 'Nessun dato', low_volume: 'Volume sotto la media',
  high_volume: 'Volume sopra la media', format_change: 'Formato cambiato',
  parse_failures: 'Log non interpretabili', source_silent: 'Sorgente silente', low_diversity: 'Bassa diversità',
};

// Stesse tre soglie del grafico di composizione (70 / 40): un case con il badge rosso deve essere
// esattamente uno di quelli contati nel segmento rosso, altrimenti i due elementi della stessa
// pagina racconterebbero due storie diverse. I colori restano quelli dell'interfaccia (Tailwind)
// invece degli esadecimali della palette grafica, perché qui il testo dev'essere leggibile dentro
// il badge: il verde della palette non regge testo bianco a questa dimensione.
function scoreStyle(score: number): string {
  if (score >= 70) return 'bg-red-600 text-white';
  if (score >= 40) return 'bg-amber-400 text-amber-950';
  return 'bg-emerald-400/15 text-emerald-300';
}

function formatAgo(iso: string | null): string {
  if (!iso) return '—';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'ora';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  return `${Math.floor(hours / 24)}g fa`;
}

// Composizione della coda per banda di priorità. Barra unica con segmenti proporzionali invece di
// tre numeri separati: la domanda vera è "quanta parte del mio carico è urgente", cioè una
// proporzione, e una proporzione si legge meglio come un'unica lunghezza divisa che come tre
// conteggi da confrontare a mente. Costruita con div in flex invece che con la libreria grafica:
// per una barra sola il controllo diretto su stacchi da 2px, angoli arrotondati ed etichette è più
// preciso, e non serve un motore di assi per qualcosa che non ha assi.
function PriorityComposition({ bands, actions }: { bands: { critical: number; medium: number; low: number }; actions: Record<string, number> }) {
  const total = bands.critical + bands.medium + bands.low;
  const segments = [
    { key: 'critical', label: 'Critica', hint: 'punteggio 70+', value: bands.critical, color: BAND_COLORS.critical, ink: '#ffffff' },
    { key: 'medium', label: 'Media', hint: '40-69', value: bands.medium, color: BAND_COLORS.medium, ink: '#3d2c00' },
    { key: 'low', label: 'Bassa', hint: 'sotto 40', value: bands.low, color: BAND_COLORS.low, ink: '#ffffff' },
  ].filter(s => s.value > 0);

  return (
    <div className="rounded-lg border border-[#23314d] bg-[#151f33] px-4 py-3">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-medium text-sm text-[#e8edf7]">Composizione della coda</h2>
        <span className="text-xs text-[#6b7f9e]">{total} case aperti valutati</span>
      </div>

      {total === 0 ? (
        <p className="text-sm text-[#6b7f9e] py-3">Nessun case aperto da valutare.</p>
      ) : (
        <>
          <div className="flex gap-0.5 h-9" role="img"
            aria-label={segments.map(s => `${s.label}: ${s.value}`).join(', ')}>
            {segments.map((s, i) => (
              <div key={s.key}
                style={{ backgroundColor: s.color, flexGrow: s.value, color: s.ink }}
                className={`flex items-center justify-center text-xs font-semibold min-w-[2rem] ring-1 ring-black/5
                  ${i === 0 ? 'rounded-l' : ''} ${i === segments.length - 1 ? 'rounded-r' : ''}`}>
                {/* Il valore è scritto dentro il segmento: la banda gialla sta sotto il rapporto di
                    contrasto minimo sul bianco, quindi il colore non deve mai essere l'unico
                    portatore dell'informazione. */}
                {s.value}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {segments.map(s => (
              <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-[#9fb0cc]">
                <span className="w-2.5 h-2.5 rounded-sm ring-1 ring-black/5" style={{ backgroundColor: s.color }} />
                {s.label} <span className="text-[#6b7f9e]">({s.hint})</span>
              </span>
            ))}
          </div>

          {/* Che TIPO di lavoro aspetta l'analista: tre isolamenti host non sono trenta report. */}
          <div className="mt-3 pt-3 border-t border-[#23314d]">
            <p className="text-xs text-[#9fb0cc] mb-1.5">Azioni suggerite sulla coda</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(actions).sort((a, b) => b[1] - a[1]).map(([action, count]) => (
                <span key={action} className="inline-flex items-center gap-1.5 text-xs bg-[#1c2942] border border-[#23314d] rounded-md px-2 py-1">
                  <span className="font-semibold text-[#e8edf7]">{count}</span>
                  <span className="text-[#9fb0cc]">{ACTION_LABELS[action] ?? action}</span>
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Aperti vs chiusi negli ultimi 7 giorni: dice se la coda sta crescendo o smaltendo, cosa che né il
// conteggio istantaneo né la composizione per priorità raccontano. Due serie della stessa unità di
// misura (numero di case) su un unico asse — mai due scale diverse sullo stesso grafico.
function QueueTrend({ trend }: { trend: { day: string; opened: string; closed: string }[] }) {
  const data = trend.map(r => ({
    giorno: new Date(r.day).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' }),
    Aperti: Number(r.opened),
    Chiusi: Number(r.closed),
  }));
  const hasData = data.some(d => d.Aperti > 0 || d.Chiusi > 0);

  return (
    <div className="rounded-lg border border-[#23314d] bg-[#151f33] px-4 py-3">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-medium text-sm text-[#e8edf7]">Aperti e chiusi, ultimi 7 giorni</h2>
        <span className="text-xs text-[#6b7f9e]">il carico sta crescendo o calando</span>
      </div>
      {!hasData ? (
        <p className="text-sm text-[#6b7f9e] py-3">Nessun case aperto o chiuso nella settimana.</p>
      ) : (
        <div style={{ height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -24 }} barGap={2}>
              <CartesianGrid stroke={GRID_INK} vertical={false} />
              <XAxis dataKey="giorno" tick={{ fontSize: 11, fill: AXIS_INK }} tickLine={false} axisLine={{ stroke: GRID_INK }} />
              <YAxis tick={{ fontSize: 11, fill: AXIS_INK }} tickLine={false} axisLine={false} allowDecimals={false} width={44} />
              <Tooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${GRID_INK}` }} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={9} />
              {/* Animazione disattivata: su una dashboard che si ricarica a ogni azione il
                  movimento non aggiunge nulla e ritarda la lettura. (Ha anche un effetto pratico:
                  le barre animate non compaiono affatto in un browser headless, dove il ciclo di
                  animazione resta fermo — cosa emersa verificando la pagina con uno screenshot.) */}
              <Bar dataKey="Aperti" fill={SERIES_OPENED} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="Chiusi" fill={SERIES_CLOSED} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

interface ReportTarget { tenantId: number; partnerId: number; tenantName: string; caseId?: number; priority?: CasePriority }

// Invio di un report al cliente senza lasciare la dashboard: riusa interamente la pipeline di
// reportistica già esistente (template del partner + POST /api/reports/generate, che genera il PDF
// e opzionalmente lo spedisce all'indirizzo di contatto del tenant). Quando parte da un case,
// registra anche la decisione come 'send_report', così l'azione resta tracciata come tutte le altre.
function ReportModal({ target, onClose, onSent }: { target: ReportTarget; onClose: () => void; onSent: () => void }) {
  const [templates, setTemplates] = useState<{ id: number; name: string }[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [periodStart, setPeriodStart] = useState(() => new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    axios.get('/api/report-templates', { params: { partnerId: target.partnerId } })
      .then(res => {
        setTemplates(res.data.data);
        if (res.data.data[0]) setTemplateId(String(res.data.data[0].id));
      })
      .catch(err => setError(err.response?.data?.error || 'Impossibile caricare i template del partner'));
  }, [target.partnerId]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post('/api/reports/generate', {
        tenantId: target.tenantId,
        templateId: Number(templateId),
        periodStart, periodEnd,
        sendEmailCopy: sendEmail,
      });
      const report = res.data.data;
      const delivered = report?.delivered_to;
      if (target.caseId) {
        await axios.post('/api/soc-dashboard/decisions', {
          caseId: target.caseId,
          chosenAction: 'send_report',
          suggestedAction: target.priority?.suggestedAction,
          suggestedScore: target.priority?.score,
          suggestedRationale: target.priority?.rationale,
          note: `Report #${report?.id} ${delivered ? `inviato a ${delivered}` : 'generato (non inviato via email)'}`,
        });
      }
      setDone(delivered ? `Report generato e inviato a ${delivered}.` : 'Report generato: lo trovi in Reports (nessuna email inviata).');
      onSent();
    } catch (err: any) {
      setError(err.response?.data?.error || "Errore durante la generazione del report");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#23314d]">
          <h2 className="font-semibold text-[#e8edf7] inline-flex items-center gap-2">
            <FileText size={16} /> Invia report a {target.tenantName}
          </h2>
          <button onClick={onClose} className="text-[#6b7f9e] hover:text-[#9fb0cc]"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-5 space-y-4">
            <p className="text-sm text-emerald-700 inline-flex items-start gap-2">
              <CircleCheck size={16} className="mt-0.5 shrink-0" /> {done}
            </p>
            <div className="flex justify-end">
              <button onClick={onClose} className="btn-primary">Chiudi</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-3 text-sm">
              <label className="block">
                <span className="text-[#9fb0cc] text-xs">Template</span>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)} className="rounded-lg border border-[#23314d] bg-[#0b1220] text-[#e8edf7] placeholder:text-[#6b7f9e] px-2 py-1 w-full mt-1">
                  {templates.length === 0 && <option value="">Nessun template configurato per questo partner</option>}
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[#9fb0cc] text-xs">Dal</span>
                  <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="rounded-lg border border-[#23314d] bg-[#0b1220] text-[#e8edf7] placeholder:text-[#6b7f9e] px-2 py-1 w-full mt-1" />
                </label>
                <label className="block">
                  <span className="text-[#9fb0cc] text-xs">Al</span>
                  <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="rounded-lg border border-[#23314d] bg-[#0b1220] text-[#e8edf7] placeholder:text-[#6b7f9e] px-2 py-1 w-full mt-1" />
                </label>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
                <span>Invia per email all'indirizzo di contatto del cliente</span>
              </label>
              <p className="text-xs text-[#6b7f9e] inline-flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0" />
                La generazione richiede qualche secondo (il PDF viene composto lato server). Senza
                spunta il report resta disponibile nella sezione Reports senza essere spedito.
              </p>
              {error && <p className="text-xs text-red-300 inline-flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {error}
              </p>}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={onClose} className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border border-[#23314d] bg-[#1c2942] text-[#cbd7ea] hover:bg-[#23314d]">Annulla</button>
              <button onClick={send} disabled={busy || !templateId} className="btn-primary disabled:opacity-40">
                {busy ? 'Generazione…' : sendEmail ? 'Genera e invia' : 'Genera'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Riquadro compatto per una categoria urgente: conteggio sempre visibile, dettaglio a scomparsa.
function UrgentCard({ icon, title, count, tone, children }: {
  icon: React.ReactNode; title: string; count: number; tone: 'red' | 'amber' | 'slate'; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const toneClass = count === 0
    ? 'border-[#23314d] bg-[#151f33]'
    : tone === 'red' ? 'border-red-500/30 bg-red-500/10' : tone === 'amber' ? 'border-amber-500/30 bg-amber-500/10' : 'border-[#23314d] bg-[#1c2942]';
  return (
    <div className={`rounded-lg border ${toneClass}`}>
      <button onClick={() => setOpen(o => !o)} disabled={count === 0}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left disabled:cursor-default">
        <span className={count === 0 ? 'text-[#3d4d6b]' : tone === 'red' ? 'text-red-400' : tone === 'amber' ? 'text-amber-400' : 'text-[#9fb0cc]'}>
          {icon}
        </span>
        <span className="text-xs font-medium text-[#cbd7ea] flex-1">{title}</span>
        <span className={`text-2xl font-semibold tabular-nums ${count === 0 ? 'text-[#3d4d6b]' : 'text-[#e8edf7]'}`}>{count}</span>
        {count > 0 && (open ? <ChevronDown size={14} className="text-[#6b7f9e]" /> : <ChevronRight size={14} className="text-[#6b7f9e]" />)}
      </button>
      {open && count > 0 && <div className="px-3 pb-3 text-xs border-t border-[#23314d]/70 pt-2">{children}</div>}
    </div>
  );
}

// Riga della coda: punteggio, perché, azione suggerita e i due pulsanti di decisione.
function QueueRow({ item, onDecided, onRequestReport }: {
  item: QueuedCase; onDecided: () => void; onRequestReport: (t: ReportTarget) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [altAction, setAltAction] = useState('contact_customer');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const suggestion = item.priority.suggestedAction;
  const isSoar = SOAR_ACTIONS.includes(suggestion);

  async function decide(chosenAction: string, noteText?: string) {
    // "Invia report" non è solo una decisione da registrare: è un'azione che la piattaforma sa
    // davvero eseguire, quindi apre la finestra di invio invece di limitarsi a scrivere nel
    // registro (la decisione viene poi registrata dalla finestra stessa, a invio riuscito).
    if (chosenAction === 'send_report') {
      onRequestReport({
        tenantId: item.tenant_id, partnerId: item.partner_id, tenantName: item.tenant_name,
        caseId: item.id, priority: item.priority,
      });
      setChoosing(false);
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/soc-dashboard/decisions', {
        caseId: item.id,
        chosenAction,
        note: noteText || undefined,
        suggestedAction: suggestion,
        suggestedScore: item.priority.score,
        suggestedRationale: item.priority.rationale,
      });
      setChoosing(false);
      setNote('');
      onDecided();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-[#23314d] first:border-t-0">
      <div className="flex items-start gap-3 px-4 py-3">
        <span className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold ${scoreStyle(item.priority.score)}`}
          title="Priorità calcolata dai segnali elencati sotto">
          {item.priority.score}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/incidents/${item.id}`} className="font-medium text-[#e8edf7] hover:text-sky-400 hover:underline truncate">
              {item.title}
            </Link>
            <span className={`badge ${SEVERITY_BADGE[item.severity]}`}>{item.severity}</span>
            <OutcomeBadge outcome={item.outcome} />
            {item.business_critical && <Star size={11} className="text-amber-500" fill="currentColor" />}
            {/* Il cliente è informazione primaria in vista "tutti i tenant", non una nota a
                margine: prima era grigio chiarissimo e passava inosservato. */}
            <span className="badge bg-[#23314d] text-[#cbd7ea] font-medium">{item.tenant_name}</span>
            {item.status === 'new' && !item.acknowledged_at && (
              <span className="badge bg-sky-400/15 text-sky-300">mai preso in carico</span>
            )}
            {item.assigned_user && (
              <span className="badge bg-emerald-400/15 text-emerald-300" title="Assegnato con Conferma/Diversamente dalla coda o dalla pagina Incidents">in carico: {item.assigned_user}</span>
            )}
          </div>

          <div className="text-xs text-[#9fb0cc] mt-0.5">
            {item.entity_host && (item.asset_id
              ? <Link to={`/asset-center/${item.asset_id}`} className="text-sky-400 hover:underline">{item.entity_host}</Link>
              : <span>{item.entity_host}</span>)}
            {' · '}{item.alert_count} alert · aperto {formatAgo(item.created_at)}
          </div>

          <div className="mt-2 flex items-start gap-2 flex-wrap">
            <span className="badge bg-primary-50 text-sky-300 border border-sky-400/20">
              Suggerito: {ACTION_LABELS[suggestion] ?? suggestion}
            </span>
            {isSoar && <span className="badge bg-gray-100 text-[#9fb0cc]">SOAR non ancora disponibile</span>}
            <button onClick={() => setExpanded(e => !e)} className="text-xs text-[#9fb0cc] hover:text-[#cbd7ea] inline-flex items-center gap-1">
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />} perché
            </button>
          </div>

          {expanded && (
            <div className="mt-2 rounded-md bg-[#1c2942] border border-[#23314d] p-2.5 space-y-1.5">
              <p className="text-xs text-[#9fb0cc]">{item.priority.rationale}</p>
              <ul className="text-xs text-[#9fb0cc] space-y-0.5">
                {item.priority.factors.map(f => (
                  <li key={f.label} className="flex justify-between gap-3">
                    <span>{f.label}</span>
                    <span className="font-mono text-[#6b7f9e]">+{f.points}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.last_decision && (
            <p className="mt-2 text-xs text-[#9fb0cc]">
              Già valutato: <span className="font-medium text-[#cbd7ea]">{ACTION_LABELS[item.last_decision.chosen_action] ?? item.last_decision.chosen_action}</span>
              {item.last_decision.user_name ? ` da ${item.last_decision.user_name}` : ''} {formatAgo(item.last_decision.created_at)}
              {item.last_decision.note ? ` — "${item.last_decision.note}"` : ''}
            </p>
          )}

          {choosing && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={altAction} onChange={e => setAltAction(e.target.value)} className="rounded-lg border border-[#23314d] bg-[#0b1220] text-[#e8edf7] placeholder:text-[#6b7f9e] px-2 py-1 py-1 px-2 text-xs">
                {Object.entries(ACTION_LABELS).filter(([k]) => k !== suggestion).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
              <input value={note} onChange={e => setNote(e.target.value)} className="rounded-lg border border-[#23314d] bg-[#0b1220] text-[#e8edf7] placeholder:text-[#6b7f9e] px-2 py-1 py-1 px-2 text-xs flex-1 min-w-[180px]"
                placeholder={altAction === 'other' ? 'Descrivi cosa hai fatto (obbligatorio)…' : 'Nota (facoltativa)…'} />
              <button onClick={() => decide(altAction, note)} disabled={saving || (altAction === 'other' && !note.trim())}
                className="btn-primary text-xs py-1 px-2 disabled:opacity-40">Registra</button>
              <button onClick={() => setChoosing(false)} className="text-xs text-[#9fb0cc] hover:text-[#cbd7ea]">annulla</button>
            </div>
          )}
        </div>

        {!choosing && (
          <div className="shrink-0 flex flex-col gap-1.5">
            <button onClick={() => decide(suggestion)} disabled={saving}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-sky-500 text-slate-950 hover:bg-sky-400 disabled:opacity-40">
              <Check size={12} /> Conferma
            </button>
            <button onClick={() => setChoosing(true)} disabled={saving}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-[#23314d] text-[#9fb0cc] hover:bg-[#1c2942]">
              <X size={12} /> Diversamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Dashboard operativa del SOC (2026-09-15): distinta da Overview, che resta la vista di andamento
// generale (health score, trend). Qui c'è solo lavoro: in alto cosa richiede azione ora, sotto il
// contesto di contorno. I suggerimenti di priorità/azione sono calcolati da regole deterministiche
// (backend services/casePriority.ts), NON da un modello: l'interfaccia lo dichiara esplicitamente
// per non far sembrare un parere quello che è una formula — quando arriverà la Fase 2 (LLM
// self-hosted, vedi docs/ARCHITECTURE.md) cambierà la sorgente del suggerimento, non questa UI.
export default function SocOpsPage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/soc-dashboard', { params: { tenantId: selectedTenantId ?? undefined } });
      setData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  const u = data?.urgent;
  const s = data?.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-[#e8edf7] inline-flex items-center gap-2">
              <Siren size={20} /> SOC Operations
            </h1>
            <TenantSelect variant="console" />
          </div>
          <p className="text-sm text-[#9fb0cc] mt-1">
            Cosa richiede attenzione adesso, in ordine di priorità. Per l'andamento generale e i trend
            resta <Link to="/overview" className="text-sky-400 hover:underline">Overview</Link>.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border border-[#23314d] bg-[#1c2942] text-[#cbd7ea] hover:bg-[#23314d] flex items-center gap-1 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Aggiorna
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
      {loading && !data && <div className="rounded-lg border border-[#23314d] bg-[#151f33] px-4 py-6 text-sm text-[#6b7f9e]">Caricamento…</div>}

      {u && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            <UrgentCard icon={<ServerCrash size={15} />} title="Asset critici offline" count={u.criticalAssetsOffline.length} tone="red">
              <ul className="space-y-1">
                {u.criticalAssetsOffline.slice(0, 8).map(a => (
                  <li key={a.case_id} className="flex justify-between gap-2">
                    <Link to={`/incidents/${a.case_id}`} className="text-sky-400 hover:underline truncate">{a.entity_host}</Link>
                    <span className="text-[#6b7f9e] shrink-0">{formatAgo(a.created_at)}</span>
                  </li>
                ))}
                {u.criticalAssetsOffline.length > 8 && <li className="text-[#6b7f9e]">+{u.criticalAssetsOffline.length - 8} altri</li>}
              </ul>
            </UrgentCard>

            <UrgentCard icon={<Radio size={15} />} title="Broker offline" count={u.brokersOffline.length} tone="red">
              <ul className="space-y-1">
                {u.brokersOffline.slice(0, 8).map(b => (
                  <li key={b.id} className="flex justify-between gap-2">
                    <span className="truncate">{b.name} <span className="text-[#6b7f9e]">· {b.tenant_name}</span></span>
                    <span className="text-[#6b7f9e] shrink-0">{b.offline_minutes ? `${b.offline_minutes} min` : 'mai visto'}</span>
                  </li>
                ))}
                {u.brokersOffline.length > 8 && <li className="text-[#6b7f9e]">+{u.brokersOffline.length - 8} altri</li>}
              </ul>
            </UrgentCard>

            <UrgentCard icon={<Activity size={15} />} title="Anomalie ingestion" count={u.dataQualityIssues.length} tone="amber">
              <ul className="space-y-1">
                {u.dataQualityIssues.slice(0, 8).map(q => (
                  <li key={q.id}>
                    <span className={q.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}>
                      {DQ_TYPE_LABELS[q.type] ?? q.type}
                    </span>
                    <span className="text-[#6b7f9e]"> · {q.tenant_name}</span>
                  </li>
                ))}
                {u.dataQualityIssues.length > 8 && <li className="text-[#6b7f9e]">+{u.dataQualityIssues.length - 8} altre</li>}
              </ul>
              <Link to="/data-quality" className="text-sky-400 hover:underline mt-1 inline-block">Apri Data Quality →</Link>
            </UrgentCard>

            <UrgentCard icon={<ShieldAlert size={15} />} title="Endpoint compromessi" count={u.compromisedEndpoints.length} tone="red">
              <ul className="space-y-1">
                {u.compromisedEndpoints.map(e => (
                  <li key={`${e.asset_id}-${e.vendor}`} className="flex justify-between gap-2">
                    <Link to={`/asset-center/${e.asset_id}`} className="text-sky-400 hover:underline truncate">{e.hostname}</Link>
                    <span className="shrink-0 text-[#6b7f9e]">{e.infected ? 'infetto' : 'isolato'}</span>
                  </li>
                ))}
              </ul>
            </UrgentCard>

            <UrgentCard icon={<PlugZap size={15} />} title="Integrazioni ferme" count={u.integrationIssues.length} tone="amber">
              <ul className="space-y-1">
                {u.integrationIssues.map((i, idx) => (
                  <li key={idx}>
                    <span className="text-[#cbd7ea]">{i.label}</span>
                    <span className="text-[#6b7f9e]"> · {i.scope_name}</span>
                    <div className="text-[#6b7f9e] truncate">
                      {i.last_sync_status === 'error' ? (i.last_error || 'errore') : `ultima sync ${formatAgo(i.last_sync_at)}`}
                    </div>
                  </li>
                ))}
              </ul>
            </UrgentCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <PriorityComposition bands={u.priorityBands} actions={u.suggestedActions} />
            {s && <QueueTrend trend={s.trend} />}
          </div>

          <div className="rounded-lg border border-[#23314d] bg-[#151f33] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#23314d] flex items-center justify-between">
              <div>
                <h2 className="font-medium text-sm text-[#e8edf7]">Coda di intervento</h2>
                <p className="text-xs text-[#6b7f9e] inline-flex items-center gap-1">
                  <Info size={11} />
                  Priorità e azione suggerite da regole deterministiche sui segnali della piattaforma, non da un modello AI
                  {data.meta.queueTruncated && ' · elenco troncato ai case più recenti'}
                </p>
              </div>
              <span className="text-xs text-[#6b7f9e]">{u.priorityQueue.length} in coda</span>
            </div>
            {u.priorityQueue.length === 0 && (
              <div className="px-4 py-6 text-sm text-[#6b7f9e]">Nessun case aperto da lavorare in questo scope.</div>
            )}
            {u.priorityQueue.map(item => <QueueRow key={item.id} item={item} onDecided={load} onRequestReport={setReportTarget} />)}
          </div>
        </>
      )}

      {reportTarget && (
        <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} onSent={load} />
      )}

      {s && (
        <div className="rounded-lg border border-[#23314d] bg-[#151f33] px-4 py-3">
          <h2 className="font-medium text-sm text-[#e8edf7] mb-2">Sommario</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {[
              ['Case aperti', s.cases.cases_open], ['Mai presi in carico', s.cases.cases_new],
              ['Nuovi nelle 24h', s.cases.cases_24h], ['Chiusi nelle 24h', s.cases.cases_closed_24h],
              ['Asset totali', s.assets.assets_total], ['Business critical', s.assets.assets_business_critical],
              ['Nuovi asset oggi', s.assets.assets_new_today], ['Vulnerabilità high/critical', s.assets.vulnerabilities_high],
            ].map(([label, value]) => (
              <div key={label as string}>
                <div className="text-lg font-semibold text-[#e8edf7]">{value}</div>
                <div className="text-xs text-[#9fb0cc]">{label}</div>
              </div>
            ))}
          </div>
          {s.ruleCounters.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#23314d]">
              <p className="text-xs text-[#9fb0cc] mb-1">Rumore atteso conteggiato oggi (nessun case aperto):</p>
              <div className="flex flex-wrap gap-2">
                {s.ruleCounters.map(c => (
                  <span key={c.name} className="badge bg-[#23314d] text-[#9fb0cc]">
                    {Number(c.count).toLocaleString('it-IT')} · {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
